import { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import clsx from 'clsx';

export interface SelectOption {
  value: string;
  label: string;
  /** Extra text shown in lighter color after the label */
  secondary?: string;
}

interface SearchableSelectProps {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export default function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  className,
  disabled,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [highlightIdx, setHighlightIdx] = useState(0);

  const selected = options.find((o) => o.value === value);

  const filtered = query
    ? options.filter((o) => {
        const q = query.toLowerCase();
        return o.label.toLowerCase().includes(q) || (o.secondary?.toLowerCase().includes(q));
      })
    : options;

  // Click outside → close
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setQuery('');
      setHighlightIdx(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  // Scroll highlighted into view
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.children[highlightIdx] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlightIdx, open]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!open) {
        if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setOpen(true);
        }
        return;
      }
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightIdx((i) => Math.min(i + 1, filtered.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightIdx((i) => Math.max(i - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (filtered[highlightIdx]) {
            onChange(filtered[highlightIdx].value);
            setOpen(false);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setOpen(false);
          break;
      }
    },
    [open, filtered, highlightIdx, onChange],
  );

  return (
    <div ref={containerRef} className={clsx('relative', className)} onKeyDown={handleKeyDown}>
      {/* Trigger */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={clsx(
          'w-full flex items-center justify-between gap-2 px-3 py-2 text-sm border rounded-lg shadow-sm bg-white transition-colors duration-150',
          'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
          disabled ? 'opacity-50 cursor-not-allowed border-gray-200 bg-gray-50' : 'border-gray-300 cursor-pointer hover:border-gray-400',
        )}
      >
        <span className={clsx('truncate', selected ? 'text-gray-900' : 'text-gray-400')}>
          {selected ? (
            <>
              {selected.label}
              {selected.secondary && <span className="text-gray-400 ml-1">({selected.secondary})</span>}
            </>
          ) : (
            placeholder
          )}
        </span>
        <ChevronDown className={clsx('w-4 h-4 flex-shrink-0 text-gray-400 transition-transform', open && 'rotate-180')} />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          {/* Search input */}
          <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100">
            <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              className="flex-1 text-sm bg-transparent outline-none placeholder-gray-400"
              placeholder="Type to search..."
              value={query}
              onChange={(e) => { setQuery(e.target.value); setHighlightIdx(0); }}
            />
            {query && (
              <button type="button" onClick={() => { setQuery(''); inputRef.current?.focus(); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Options list */}
          <div ref={listRef} className="max-h-60 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-sm text-gray-400 text-center">No results found</div>
            ) : (
              filtered.map((opt, idx) => (
                <button
                  key={opt.value}
                  type="button"
                  className={clsx(
                    'w-full text-left px-3 py-2 text-sm transition-colors',
                    idx === highlightIdx && 'bg-primary-50',
                    opt.value === value ? 'text-primary-700 font-medium bg-primary-50/60' : 'text-gray-700',
                    'hover:bg-primary-50',
                  )}
                  onMouseEnter={() => setHighlightIdx(idx)}
                  onClick={() => { onChange(opt.value); setOpen(false); }}
                >
                  {opt.label}
                  {opt.secondary && <span className="text-gray-400 ml-1 text-xs">({opt.secondary})</span>}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
